/**
 * Tracking Helper Utilities
 * Reusable functions to simplify common tracking patterns
 */

import mixpanelService from '../mixpanel.service';

/**
 * Get current environment from env vars
 */
export const getCurrentEnvironment = () => {
  return (import.meta.env.VITE_APP_ENV || 'development') as 'development' | 'qa' | 'production';
};

/**
 * Get current tenant ID from env vars
 */
export const getCurrentTenantId = () => {
  return import.meta.env.VITE_DEFAULT_TENANT || '';
};

/**
 * Track navigation click with standard properties
 */
export const trackNavigationClick = (menuItem: string, destinationUrl: string, menuType: 'sidebar' | 'topbar' | 'mobile' = 'sidebar') => {
  mixpanelService.track('Portal navigation_click', {
    menu_item: menuItem,
    destination_url: destinationUrl,
    menu_type: menuType,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track modal opening
 */
export const trackModalOpened = (modalId: string, modalTitle: string) => {
  mixpanelService.track('Portal modal_opened', {
    modal_id: modalId,
    modal_title: modalTitle,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track form submission
 */
export const trackFormSubmitted = (formName: string) => {
  mixpanelService.track('Portal form_submitted', {
    form_name: formName,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track company switch
 */
export const trackCompanySwitch = (
  newCompanyId: string | number,
  newCompanyName: string,
  previousCompanyId: string | number
) => {
  // Update super properties
  mixpanelService.registerSuperProperties({
    company_id: newCompanyId,
  });

  // Update user properties
  mixpanelService.setUserProperties({
    company_id: newCompanyId,
    company_name: newCompanyName,
  });

  // Track the event
  mixpanelService.track('Portal company_switched', {
    new_company_id: newCompanyId,
    new_company_name: newCompanyName,
    previous_company_id: previousCompanyId,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track invoice view with standard properties
 */
export const trackInvoiceView = (
  invoiceId: string | number | undefined,
  invoiceNumber: string | undefined,
  documentType: string,
  amount: number,
  companyId: string | number,
  isCorrection: boolean = false
) => {
  if (!invoiceId || !invoiceNumber) return; // Skip if no ID or number

  mixpanelService.track('Portal invoice_viewed', {
    invoice_id: invoiceId,
    invoice_number: invoiceNumber,
    document_type: documentType,
    amount,
    company_id: companyId,
    is_correction: isCorrection,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track credit note view
 */
export const trackCreditNoteView = (
  invoiceId: string | number | undefined,
  invoiceNumber: string | undefined,
  originalAmount: number,
  companyId: string | number
) => {
  if (!invoiceId || !invoiceNumber) return; // Skip if no ID or number

  mixpanelService.track('Portal credit_note_viewed', {
    invoice_id: invoiceId,
    invoice_number: invoiceNumber,
    original_amount: originalAmount,
    company_id: companyId,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track invoice submission success
 */
export const trackInvoiceSubmission = (params: {
  invoiceNumber: string;
  documentType: string;
  customerName: string;
  totalAmount: number;
  itemsCount: number;
  paymentMethod: string;
  receiverType: string;
  isCorrection: boolean;
  companyId: string | number;
}) => {
  // Increment user's invoice counter
  mixpanelService.incrementUserProperty('invoices_created', 1);

  // Track the submission event
  mixpanelService.track('Portal invoice_submitted', {
    invoice_number: params.invoiceNumber,
    document_type: params.documentType,
    customer_name: params.customerName,
    total_amount: params.totalAmount,
    items_count: params.itemsCount,
    payment_method: params.paymentMethod,
    receiver_type: params.receiverType,
    is_correction: params.isCorrection,
    company_id: params.companyId,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track invoice submission failure
 */
export const trackInvoiceSubmissionFailed = (
  errorMessage: string,
  documentType: string,
  companyId: string | number,
  extra?: {
    responseCode?: string;
    errorSource?: 'backend_rejection' | 'exception';
    httpStatus?: number;
    errorDetailCodes?: string[];
  }
) => {
  // Fallback: si no llega companyId, usar la compañía seleccionada del storage
  // (evita el clásico company_id: 0 que dejaba el evento sin contexto).
  const resolvedCompanyId =
    companyId || (typeof localStorage !== 'undefined' ? localStorage.getItem('selectedCompanyId') : '') || 0;

  mixpanelService.track('Portal invoice_submission_failed', {
    error_message: errorMessage,
    document_type: documentType,
    company_id: resolvedCompanyId,
    environment: getCurrentEnvironment(),
    response_code: extra?.responseCode,
    error_source: extra?.errorSource,
    http_status: extra?.httpStatus,
    error_detail_codes: extra?.errorDetailCodes,
  });
};

/**
 * Track certificate modal flow
 */
export const trackCertificateModal = {
  opened: (page: string) => {
    mixpanelService.track('Portal certificate_modal_opened', {
      page,
      environment: getCurrentEnvironment(),
    });
  },

  hasSignature: (page: string) => {
    mixpanelService.track('Portal certificate_has_signature', {
      page,
      environment: getCurrentEnvironment(),
    });
  },

  needsSignature: (page: string) => {
    mixpanelService.track('Portal certificate_needs_signature', {
      page,
      environment: getCurrentEnvironment(),
    });
  },

  gotoConfiguration: (page: string, destination: string) => {
    mixpanelService.track('Portal certificate_goto_configuration', {
      page,
      destination,
      environment: getCurrentEnvironment(),
    });
  },

  guideStepViewed: (step: number, stepName: string) => {
    mixpanelService.track('Portal certificate_guide_step_viewed', {
      step,
      step_name: stepName,
      environment: getCurrentEnvironment(),
    });
  },

  guideCompleted: (action: string) => {
    mixpanelService.track('Portal certificate_guide_completed', {
      action,
      environment: getCurrentEnvironment(),
    });
  },

  linkClicked: (linkType: string, linkUrl: string, step: number) => {
    mixpanelService.track('Portal certificate_link_clicked', {
      link_type: linkType,
      link_url: linkUrl,
      step,
      environment: getCurrentEnvironment(),
    });
  },
};

/**
 * Set user profile after authentication
 */
export const setUserProfile = (user: {
  id: string | number;
  email: string;
  name: string;
  roles?: string[];
  companyId?: string | number;
  companyName?: string;
}) => {
  // Identify the user
  mixpanelService.identify(user.id);

  // Set user properties
  mixpanelService.setUserProperties({
    $email: user.email,
    $name: user.name,
    user_id: user.id,
    full_name: user.name,
    roles: user.roles,
    company_id: user.companyId,
    company_name: user.companyName,
    last_login: new Date().toISOString(),
  });

  // Set first-time properties
  mixpanelService.setUserPropertyOnce({
    created_at: new Date().toISOString(),
  });

  // Register super properties
  mixpanelService.registerSuperProperties({
    tenant_id: getCurrentTenantId(),
    company_id: user.companyId,
  });
};

/**
 * Clear user profile on logout
 */
export const clearUserProfile = () => {
  mixpanelService.reset();
};

/**
 * Track plans page viewed
 */
export const trackPlansViewed = (params: {
  hasActiveSubscription: boolean;
  currentPlan?: string;
  availablePlansCount: number;
}) => {
  mixpanelService.track('Portal plans_viewed', {
    has_active_subscription: params.hasActiveSubscription,
    current_plan: params.currentPlan,
    available_plans_count: params.availablePlansCount,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track plan selection
 */
export const trackPlanSelected = (params: {
  planId: string;
  planName: string;
  planPrice: number;
  planInterval: string;
  currentPlan?: string;
  actionType: 'upgrade' | 'downgrade' | 'new';
}) => {
  mixpanelService.track('Portal plan_selected', {
    plan_id: params.planId,
    plan_name: params.planName,
    plan_price: params.planPrice,
    plan_interval: params.planInterval,
    current_plan: params.currentPlan,
    action_type: params.actionType,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track plan upgrade started
 */
export const trackPlanUpgradeStarted = (params: {
  fromPlan: string;
  toPlan: string;
  fromPrice: number;
  toPrice: number;
}) => {
  mixpanelService.track('Portal plan_upgrade_started', {
    from_plan: params.fromPlan,
    to_plan: params.toPlan,
    from_price: params.fromPrice,
    to_price: params.toPrice,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track plan downgrade started
 */
export const trackPlanDowngradeStarted = (params: {
  fromPlan: string;
  toPlan: string;
  fromPrice: number;
  toPrice: number;
}) => {
  mixpanelService.track('Portal plan_downgrade_started', {
    from_plan: params.fromPlan,
    to_plan: params.toPlan,
    from_price: params.fromPrice,
    to_price: params.toPrice,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track plan changed
 */
export const trackPlanChanged = (params: {
  planId: string;
  planName: string;
  planPrice: number;
  previousPlan?: string;
  changeType: 'upgrade' | 'downgrade' | 'new';
}) => {
  // Update user properties
  mixpanelService.setUserProperties({
    subscription_plan: params.planName,
  });

  // Track the event
  mixpanelService.track('Portal plan_changed', {
    plan_id: params.planId,
    plan_name: params.planName,
    plan_price: params.planPrice,
    previous_plan: params.previousPlan,
    change_type: params.changeType,
    environment: getCurrentEnvironment(),
  });
};

/**
 * Track payment modal opened
 */
export const trackPaymentModalOpened = (params: {
  planId: string;
  planName: string;
  amount: number;
}) => {
  mixpanelService.track('Portal payment_modal_opened', {
    plan_id: params.planId,
    plan_name: params.planName,
    amount: params.amount,
    environment: getCurrentEnvironment(),
  });
};

export default {
  getCurrentEnvironment,
  getCurrentTenantId,
  trackNavigationClick,
  trackModalOpened,
  trackFormSubmitted,
  trackCompanySwitch,
  trackInvoiceView,
  trackCreditNoteView,
  trackInvoiceSubmission,
  trackInvoiceSubmissionFailed,
  trackCertificateModal,
  setUserProfile,
  clearUserProfile,
  trackPlansViewed,
  trackPlanSelected,
  trackPlanUpgradeStarted,
  trackPlanDowngradeStarted,
  trackPlanChanged,
  trackPaymentModalOpened,
};
